import {
  EVENT_POSTER_CONSTANTS,
  EntryTypeFree,
  EventCanonicalOwnerTypeUser,
  EventStatusActive,
  EventStatusCancelled,
  FeedPlacementTypeOrganic,
  type DiscoveryEvent,
  type FeedEvent,
} from '@/lib/api/apiTypes';
import { type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';

export function mapDiscoveryEventToFeedEvent(event: DiscoveryEvent): FeedEvent {
  return {
    id: event.id,
    creator_user_id: event.host.user_id,
    title: event.title,
    description: event.description,
    venue_name: event.venue_name,
    address: event.address,
    age_restriction: event.age_restriction,
    latitude: event.coordinate.latitude,
    longitude: event.coordinate.longitude,
    starts_at: event.starts_at,
    ends_at: event.ends_at,
    capacity: 0,
    is_cancelled: event.is_cancelled,
    poster_url: event.poster_url,
    poster_images:
      event.poster_images?.length
        ? event.poster_images
        : [
            {
              id: `preview-${event.id}`,
              object_key: '',
              public_url: event.poster_url,
              position: 0,
              focal_point_x: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x,
              focal_point_y: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y,
              focal_scale: 1,
            },
          ],
    tags: event.tags ?? [],
    going_count: event.rsvp_count,
    event_momentum: event.event_momentum,
    recent_going_30m: event.recent_going_30m,
    recent_going_60m: event.recent_going_60m,
    interested_count: 0,
    friends_going_count: 0,
    viewer_follows_host: false,
    viewer_follows_venue: false,
    viewer_not_interested: false,
    viewer_interest_score: 0,
    matching_interest_tags: [],
    discover_hint: undefined,
    placement: FeedPlacementTypeOrganic,
    promotion_label: undefined,
    attendee_preview: [],
    friend_preview: [],
    comment_count: event.comment_count,
    user_has_rsvpd: event.viewer_rsvp_status === 'going',
    viewer_rsvp_status: event.viewer_rsvp_status,
    is_saved: event.is_saved,
    host_is_curator: event.host_is_curator,
    distance_meters: Math.max(0, event.distance_miles) * 1609.34,
    editorial_label: null,
    launch_badges: [],
    fiesta_score: 0,
    popularity_score: event.rsvp_count > 0 ? Math.log1p(event.rsvp_count) : 0,
    host: event.host,
    event_url: event.event_url ?? null,
    ticket_url: event.ticket_url ?? null,
    is_free: event.is_free,
    price: event.price ?? null,
    cover_price: event.cover_price ?? null,
    attributes: event.attributes ?? [],
    entry_type: event.entry_type,
    is_private: event.is_private,
    is_boosted: event.is_boosted,
    boost_tier: event.boost_tier,
    boost_expires_at: event.boost_expires_at ?? null,
    doors_at: event.doors_at ?? null,
  };
}

export function mapFeedEventToEventDetail(event: FeedEvent): RadiousEventDetail {
  // Feed-backed detail is preview initialData only; defaults stay conservative until /events/:id wins.
  return {
    id: event.id,
    title: event.title,
    description: event.description,
    venue_name: event.venue_name,
    address: event.address,
    age_restriction: event.age_restriction,
    starts_at: event.starts_at,
    ends_at: event.ends_at,
    poster_url: event.poster_url,
    tags: event.tags ?? [],
    rsvp_count: event.going_count,
    comment_count: event.comment_count,
    viewer_rsvp_status: event.viewer_rsvp_status,
    is_saved: event.is_saved,
    is_cancelled: event.is_cancelled,
    status: event.is_cancelled ? EventStatusCancelled : EventStatusActive,
    host_is_curator: event.host_is_curator,
    distance_miles: event.distance_meters / 1609.34,
    host: event.host,
    coordinate: {
      latitude: event.latitude,
      longitude: event.longitude,
    },
    summary: event.description,
    capacity: event.capacity,
    poster_images: event.poster_images?.length
      ? event.poster_images
      : [
          {
            id: `preview-${event.id}`,
            object_key: '',
            public_url: event.poster_url,
            position: 0,
            focal_point_x: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x,
            focal_point_y: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y,
            focal_scale: 1,
          },
        ],
    attendee_preview: event.attendee_preview ?? [],
    event_url: event.event_url,
    ticket_url: event.ticket_url,
    is_free: event.is_free,
    price: event.price,
    cover_price: event.cover_price ?? null,
    entry_type: event.entry_type ?? EntryTypeFree,
    cover_notes: null,
    short_description: null,
    rsvp_limit: null,
    is_rsvp_full: false,
    is_at_capacity: false,
    is_private: event.is_private ?? false,
    last_entry_time: null,
    event_hashtag: null,
    language: 'en',
    event_format: null,
    dress_code: null,
    parking_info: null,
    presented_by: null,
    weather_dependent: false,
    rain_plan: null,
    is_boosted: event.is_boosted ?? false,
    boost_expires_at: event.boost_expires_at ?? null,
    series_id: null,
    cancelled_at: event.is_cancelled ? event.ends_at ?? event.starts_at : null,
    attributes: event.attributes ?? [],
    contact_email: null,
    contact_phone: null,
    internal_notes: null,
    cancellation_message: null,
    venue_id: event.venue_id,
    canonical_owner_type: EventCanonicalOwnerTypeUser,
    is_owned_by_org: false,
    viewer_can_manage: false,
    viewer_can_cancel: false,
    venue_claimed: false,
    doors_at: event.doors_at,
    source_fixture_id: null,
    is_fixture_managed: false,
    owner_stats: null,
    published_at: event.published_at ?? null,
  };
}
